// PolicyModal — v143.5: In-app viewer for Terms & Conditions and Privacy Policy
// Fetches from API (centrally managed via admin panel).
// ★ 4.39 (P3, the default for Q-CR1): the bundled TERMS_CONTENT / PRIVACY_CONTENT are gone — the
//   database text is the one source. The fetch is LegalMarkdown's useLegalContent (one fetch for
//   every legal surface); when it fails, <LegalLoadFailed> (one sentence, "Try again", the
//   website's copy). Gate: scripts/check-legal-one-source.mjs.
// ★★ PROD-763 (Dr. Rami list-2 S11, R31 amended): for the privacy text the hook falls back to the
//   ONE bundled copy (utils/bundledPrivacy.js), so `content` is that copy when the database text
//   cannot be loaded; <LegalLoadFailed> remains for the terms. Gate: scripts/check-privacy-fallback.mjs.

import { createPortal } from 'react-dom'
import LegalMarkdown, { useLegalContent, LegalLoadFailed } from './LegalMarkdown'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'

export default function PolicyModal({ title, type, onClose }) {
  const { content, loading: loadingContent, reload } = useLegalContent(type === 'terms' ? 'terms' : 'privacy')

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div 
        className="relative w-full max-w-lg bg-white rounded-t-2xl overflow-hidden flex flex-col animate-[slideUpSheet_0.3s_ease] bottom-actions [--ba-min:0px]"
        style={{ maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes slideUpSheet { from { transform: translateY(100%); } to { transform: translateY(0); } }
        `}</style>
        {/* PROD-1186: handle, header and footer keep their height (flex-shrink-0); the scroller
            is flex-1 min-h-0 and takes what is left of the 85vh panel. It was sized
            calc(85vh - 120px) against ~154px of fixed parts plus the bottom inset, so the
            footer was clipped on a long document. */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <h2 style={{ fontSize: 16, fontWeight: 700, color: NAVY }}>{title}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 border-none cursor-pointer">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">
          {loadingContent ? (
            <div className="flex items-center justify-center py-12">
              <OrellaSpinner size={40} ringWidth={2} />
            </div>
          ) : content ? (
            <LegalMarkdown text={content} hideTitle />
          ) : (
            <LegalLoadFailed type={type === 'terms' ? 'terms' : 'privacy'} onRetry={reload} />
          )}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="w-full py-3 rounded-xl font-semibold text-sm border-none cursor-pointer" style={{ backgroundColor: ICE_BLUE, color: NAVY }}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
